import moment from 'moment';
import { TransferReservationBaseFiltersFormType } from '../../viajeTraslado/components/ViajeTrasladoBaseFilters';

export interface GestionColumnVisibility {
  schedule: boolean;
  cliente: boolean;
  puntos: boolean;
  coordinationObservation: boolean;
  general_observation: boolean;
  lltpais: boolean;
  serviceType: boolean;
  event_name: boolean;
  id: boolean;
  platformId: boolean;
  preAssignedDriver: boolean;
  assigned_driver: boolean;
  driver_price: boolean;
  business_unit: boolean;
  passengers: boolean;
  state: boolean;
  actions: boolean;
}

export const columnsLabels: Record<keyof GestionColumnVisibility, string> = {
  schedule: 'Horarios',
  cliente: 'Cliente',
  puntos: 'Domicilios',
  coordinationObservation: 'Obs. Coordinación',
  general_observation: 'Obs. Chofer',
  lltpais: 'Obs. LLT-País',
  serviceType: 'Servicio',
  event_name: 'Evento',
  id: 'ID',
  platformId: 'Plataforma',
  preAssignedDriver: 'Chofer Pre-asignado',
  assigned_driver: 'Chofer Asignado',
  driver_price: 'Precio del chofer',
  business_unit: 'Unidad de Negocio',
  passengers: 'Pasajeros',
  state: 'Estado',
  actions: 'Acciones'
};

export interface SortState {
  sortDir: string;
  sortBy: string;
}

export interface GestionReservesState {
  filters: TransferReservationBaseFiltersFormType;
  page: number;
  rowsPerPage: number;
  sort: SortState;
  columnVisibility: GestionColumnVisibility;
  lastAccessDate?: string; // Fecha del último acceso para detectar cambios de día
}

const STORAGE_KEY = 'gestionReservesState';

export const getDefaultFilters = (): TransferReservationBaseFiltersFormType => ({
  dateFrom: moment().startOf('day'),
  dateTo: moment().endOf('day'),
  clientId: null,
  serviceType: 'Todos',
  hasPriority: false,
  passangerName: '',
  client_type: 'AllClient',
  street: '',
  locality: '',
  phone: '',
  client_phone: '',
  reservationId: '',
  state: 'Todos',
  assigned_driver_id: null,
  pre_assigned_driver_id: null,
  driverCode: '',
  oficial_cuenta_id: null,
  authorized_id: null,
  cost_center: '',
  buy_order: '',
  sector: '',
  driver_with_close_shift: false,
  departure_time_exceeded: false,
  sent_reservations_platform: false,
  not_sent_reservations_platform: false,
  has_platform_id: false,
  is_event: false,
  is_llt_pais: false,
  from_app: false
});

export const getDefaultColumnVisibility = (): GestionColumnVisibility => ({
  schedule: true,
  cliente: true,
  puntos: true,
  coordinationObservation: true,
  general_observation: true,
  lltpais: true,
  serviceType: true,
  event_name: true,
  id: true,
  platformId: true,
  preAssignedDriver: true,
  assigned_driver: true,
  driver_price: true,
  business_unit: true,
  passengers: true,
  state: true,
  actions: true
});

export const getDefaultSort = (): SortState => ({
  sortDir: 'ASC',
  sortBy: 'created_at'
});

const getDefaultState = (): GestionReservesState => ({
  filters: getDefaultFilters(),
  page: 1,
  rowsPerPage: 50,
  sort: getDefaultSort(),
  columnVisibility: getDefaultColumnVisibility()
});

const deserializeState = (
  storedState: Partial<GestionReservesState>
): GestionReservesState => {
  const defaultFilters = getDefaultFilters();
  const defaultColumnVisibility = getDefaultColumnVisibility();
  const today = moment().format('YYYY-MM-DD');
  const lastAccessDate = storedState.lastAccessDate;
  const isNewDay = !lastAccessDate || lastAccessDate !== today;

  return {
    ...getDefaultState(),
    ...storedState,
    columnVisibility: {
      ...defaultColumnVisibility,
      ...(storedState.columnVisibility || {})
    },
    filters: {
      ...defaultFilters,
      ...(storedState.filters || {}),
      dateFrom: isNewDay
        ? moment().startOf('day')
        : storedState.filters?.dateFrom
          ? moment(storedState.filters.dateFrom)
          : null,
      dateTo: isNewDay
        ? moment().endOf('day')
        : storedState.filters?.dateTo
          ? moment(storedState.filters.dateTo)
          : null
    },
    lastAccessDate: today // Actualizar la fecha de acceso
  };
};

export const loadStateFromLocalStorage = (): GestionReservesState => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return deserializeState(parsed);
    }
  } catch (error) {
    console.error('Error loading gestion reserves state:', error);
  }
  return getDefaultState();
};

export const saveStateToLocalStorage = (state: GestionReservesState): void => {
  try {
    const stateWithDate = {
      ...state,
      lastAccessDate: moment().format('YYYY-MM-DD')
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stateWithDate));
  } catch (error) {
    console.error('Error saving gestion reserves state:', error);
  }
};

export const clearStateFromLocalStorage = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing gestion reserves state:', error);
  }
};

export const clearStateAndAddFilterReservationId = (
  reservationId: number
): void => {
  try {
    clearStateFromLocalStorage();
    const state = getDefaultState();
    state.filters.dateFrom = null;
    state.filters.dateTo = null;
    state.filters.reservationId = reservationId.toString();
    saveStateToLocalStorage(state);
  } catch (error) {
    console.error('Error clearing and adding filter reservation id:', error);
  }
}

